import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const DIVISION_LABEL = { women: "Women", men: "Men", all: "Women & Men" };

export default function Casting() {
  const [castings, setCastings] = useState(null);

  useEffect(() => {
    api.get("/castings")
      .then((r) => setCastings(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCastings([]));
  }, []);

  return (
    <div data-testid="casting-page" className="pt-28 lg:pt-36 pb-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <p className="label-overline text-zinc-500">Open calls · Ege Models</p>
        <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl tracking-tighter font-light mt-6 leading-[0.9]">
          Now<br/><span className="italic text-zinc-600">casting.</span>
        </h1>
        <p className="text-zinc-700 mt-8 max-w-xl leading-relaxed text-lg">
          Live searches from the studio — campaigns, editorials and open scouting
          calls we are casting for right now. Apply to a specific call below, or
          submit a general application any time.
        </p>

        <div className="mt-16">
          {castings === null ? (
            <div className="py-24 flex justify-center">
              <Loader2 size={20} className="animate-spin text-zinc-400"/>
            </div>
          ) : castings.length === 0 ? (
            <div data-testid="casting-empty" className="border-t border-black/10 pt-16 pb-8">
              <p className="font-serif text-3xl md:text-4xl tracking-tighter font-light leading-tight">
                No open calls <span className="italic text-zinc-600">right now.</span>
              </p>
              <p className="text-zinc-600 mt-4 max-w-md leading-relaxed">
                We review every general submission individually, year-round.
                When a casting matches your profile, we contact you directly.
              </p>
              <Link to="/apply" data-testid="casting-empty-apply" className="btn-primary mt-8">
                Get scouted <ArrowUpRight size={16}/>
              </Link>
            </div>
          ) : (
            <div className="border-t border-black/10">
              {castings.map((c, i) => (
                <article
                  key={c.id}
                  data-testid={`casting-row-${c.id}`}
                  className="border-b border-black/10 py-10 md:py-12 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10"
                >
                  <div className="md:col-span-2">
                    <p className="label-overline text-zinc-400">{String(i + 1).padStart(2, "0")} / Casting</p>
                    <p className="label-overline text-zinc-700 mt-2">{DIVISION_LABEL[c.division] || c.division}</p>
                  </div>
                  <div className="md:col-span-7">
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tighter font-light leading-[1.05]">
                      {c.title}
                    </h2>
                    <p className="label-overline text-zinc-500 mt-3">
                      {[c.location, c.date_text].filter(Boolean).join(" · ")}
                    </p>
                    {c.brief && (
                      <p className="text-zinc-700 mt-4 leading-relaxed max-w-2xl">{c.brief}</p>
                    )}
                    {c.requirements && (
                      <p className="text-sm text-zinc-500 mt-3 leading-relaxed max-w-2xl">
                        <span className="label-overline text-zinc-400 mr-2">Looking for</span>
                        {c.requirements}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-3 flex flex-col items-start md:items-end justify-between gap-4">
                    {c.deadline && (
                      <p className="label-overline text-zinc-500 md:text-right">{c.deadline}</p>
                    )}
                    <Link
                      to="/apply"
                      data-testid={`casting-apply-${c.id}`}
                      className="btn-ghost"
                    >
                      Apply <ArrowUpRight size={14}/>
                    </Link>
                  </div>
                </article>
              ))}

              <div className="pt-12 flex flex-wrap items-center justify-between gap-4">
                <p className="text-zinc-600 max-w-md leading-relaxed text-sm">
                  Nothing that fits? We review every general submission
                  individually — you don&#39;t need an open call to be seen.
                </p>
                <Link to="/apply" data-testid="casting-general-apply" className="btn-primary">
                  Get scouted <ArrowUpRight size={16}/>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
